import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Play, Clock, Activity, Target, Brain, Heart, Calendar } from 'lucide-react';
import { WorkoutOverview } from './WorkoutOverview';
import { WorkoutBreakdown } from './WorkoutBreakdown';
import { WorkoutInsights } from './WorkoutInsights';
import { WorkoutCoach } from './WorkoutCoach';
import { WorkoutLauncher } from '../WorkoutLauncher';
import { HealthMetrics } from '../../../types/health';

interface WorkoutDetailProps {
  workout: {
    id: string;
    name: string;
    description: string;
    level: string;
    duration: number;
    targetStrain: number;
    type: string;
    coach: {
      name: string;
      image: string;
      specialties: string[];
    };
    exercises: Array<{
      name: string;
      duration: number;
      intensity: string;
      description?: string;
    }>;
    image: string;
  };
  metrics: HealthMetrics;
  onClose: () => void;
  onStart: () => void;
  onSchedule: () => void;
}

export function WorkoutDetail({ workout, metrics, onClose, onStart, onSchedule }: WorkoutDetailProps) {
  const [showWorkout, setShowWorkout] = useState(false);

  if (showWorkout) {
    return (
      <WorkoutLauncher
        workout={{
          id: workout.id,
          name: workout.name,
          exercises: workout.exercises,
          targetStrain: workout.targetStrain
        }}
        onClose={() => setShowWorkout(false)}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto pb-24">
      {/* Header Image */}
      <div className="relative h-64 sm:h-96">
        <img
          src={workout.image}
          alt={workout.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
        
        {/* Back Button */}
        <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-background/80 to-transparent">
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-background/20 backdrop-blur-sm hover:bg-background/40 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="relative -mt-32">
        <div className="container mx-auto px-4 space-y-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="theme-card p-6 space-y-6"
          >
            {/* Title Section */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">{workout.name}</h1>
                <p className="text-muted-foreground">{workout.description}</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={onSchedule}
                  className="px-4 py-2 rounded-lg bg-muted hover:bg-muted/70 transition-colors flex items-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  Schedule
                </button>
                <button
                  onClick={() => setShowWorkout(true)}
                  className="theme-button px-6 py-3 rounded-lg flex items-center gap-2"
                >
                  <Play className="w-5 h-5" />
                  Start
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-muted/30 p-3 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Clock className="w-4 h-4" />
                  <span>Duration</span>
                </div>
                <div className="text-lg font-semibold">{workout.duration}m</div>
              </div>
              <div className="bg-muted/30 p-3 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Activity className="w-4 h-4" />
                  <span>Strain</span>
                </div>
                <div className="text-lg font-semibold">{workout.targetStrain.toFixed(1)}</div>
              </div>
              <div className="bg-muted/30 p-3 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Target className="w-4 h-4" />
                  <span>Level</span>
                </div>
                <div className="text-lg font-semibold">{workout.level}</div>
              </div>
              <div className="bg-muted/30 p-3 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Heart className="w-4 h-4" />
                  <span>Recovery</span>
                </div>
                <div className="text-lg font-semibold">{metrics.recovery}%</div>
              </div>
            </div>

            {/* Workout Overview */}
            <WorkoutOverview
              type={workout.type}
              targetStrain={workout.targetStrain}
              recovery={metrics.recovery}
              currentStrain={metrics.strain.current}
            />

            {/* Exercise Breakdown */}
            <WorkoutBreakdown exercises={workout.exercises} />

            {/* AI Insights */}
            <WorkoutInsights
              workout={workout}
              metrics={metrics}
            />

            {/* Coach Info */}
            <WorkoutCoach coach={workout.coach} />
          </motion.div>
        </div>
      </div>
    </div>
  );
}